/**
 * A spot of the graph window, in whatever view it shows: where a node made there goes. The
 * nodal view places it from a hint (a world point the layout starts it at), the class diagram
 * and the blueprint with its box's corner (`diagram`, the other boxes fixed where they are drawn
 * first, so nothing jumps), the board with its rectangle (and the frame around it).
 *
 * Windows never import each other: one dragging something onto the graph (a preset, a file)
 * asks the graph window for the spot under the pointer with {@link spotAt}, an event the graph
 * window answers (`windows/graph/spot.ts`).
 */
import type { BoardPlace } from "@protocol/BoardPlace";
import type { GraphEdit } from "@protocol/GraphEdit";

/** What a node made at a spot carries, by the view (none: the view places it itself). */
export interface SpotPlace {
  /** Its box's top-left corner in the class diagram. */
  diagram?: [number, number];
  /** Its rectangle on the board. */
  board?: BoardPlace;
  /** The frame (a group) it lands in on the board. */
  group?: string;
}

/** A spot of the graph window. */
export interface Spot {
  /** The nodal view's world point the new node starts at (`null`: another view). */
  hint: { x: number; y: number; z: number } | null;
  /** Edits going first in the same undo (the diagram's boxes fixed where they are drawn). */
  first: GraphEdit[];
  /** The place of a node of kind `kind` made there. */
  place(kind: string): SpotPlace;
}

/** The event the graph window answers with the spot under a page point. */
export const GRAPH_SPOT = "knodal-graph-spot";

/** What {@link GRAPH_SPOT} carries. */
export interface SpotAsked {
  /** The page point (like a mouse event's `clientX`/`clientY`). */
  x: number;
  y: number;
  /** Called with the spot, before the event returns. */
  answer(spot: Spot): void;
}

/** The spot of the graph window under the page point `x`, `y`, or `null` when no graph window is there. */
export function spotAt(x: number, y: number): Spot | null {
  // The element under the point: it bubbles up to the graph window holding it, whatever view
  // (the diagram has a `.stage` of its own inside the window's).
  const under = document.elementFromPoint(x, y);
  if (!under?.closest(".stage")) return null;
  let spot: Spot | null = null;
  under.dispatchEvent(new CustomEvent<SpotAsked>(GRAPH_SPOT, { bubbles: true, detail: { x, y, answer: (s) => (spot = s) } }));
  return spot;
}
